import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Users, 
  MapPin, 
  ArrowLeft,
  UserPlus,
  Shield,
  Flag,
  Ban,
  Loader2,
  Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityProfile = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectDialog, setConnectDialog] = useState(false);
  const [connectMessage, setConnectMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [id]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      const response = await axios.get(`${API}/community/profile/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(language === 'es' ? 'Perfil no encontrado' : 'Profile not found');
        navigate('/community/discover');
      } else {
        toast.error(language === 'es' ? 'Error al cargar perfil' : 'Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (connectMessage.length < 50) {
      toast.error(language === 'es' 
        ? 'El mensaje debe tener al menos 50 caracteres' 
        : 'Message must be at least 50 characters');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/community/connections/request`, {
        to_profile_id: id,
        message: connectMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequestSent(true);
      setConnectDialog(false);
      toast.success(language === 'es' ? '¡Solicitud enviada!' : 'Request sent!');
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'es' ? 'Error' : 'Failed');
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleBlock = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/community/safety/block`, {
        blocked_profile_id: id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' ? 'Usuario bloqueado' : 'User blocked');
      navigate('/community/discover');
    } catch (error) {
      toast.error(language === 'es' ? 'Error' : 'Failed');
    }
  };

  const handleReport = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/community/safety/report`, {
        reported_profile_id: id,
        category: 'suspicious',
        description: 'Reported from profile view'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' ? 'Reporte enviado' : 'Report submitted');
    } catch (error) {
      toast.error(language === 'es' ? 'Error' : 'Failed');
    }
  };

  const getIssueLabel = (issueId) => {
    const labels = {
      environmental_justice: language === 'es' ? 'Justicia Ambiental' : 'Environmental Justice',
      voting_rights: language === 'es' ? 'Derechos de Voto' : 'Voting Rights',
      housing: language === 'es' ? 'Vivienda' : 'Housing',
      reproductive_freedom: language === 'es' ? 'Libertad Reproductiva' : 'Reproductive Freedom',
      immigration: language === 'es' ? 'Inmigración' : 'Immigration',
      workers_rights: language === 'es' ? 'Derechos Laborales' : 'Workers\' Rights',
      climate_justice: language === 'es' ? 'Justicia Climática' : 'Climate Justice',
      police_accountability: language === 'es' ? 'Responsabilidad Policial' : 'Police Accountability',
      education_equity: language === 'es' ? 'Equidad Educativa' : 'Education Equity',
      healthcare_access: language === 'es' ? 'Acceso a Salud' : 'Healthcare Access',
    };
    return labels[issueId] || issueId.replace('_', ' ');
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-loading">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="community-profile">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate(-1)}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        {language === 'es' ? 'Atrás' : 'Back'}
      </Button>

      {/* Profile Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Users className="h-10 w-10 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold">
                    {profile.display_name || (language === 'es' ? 'Defensor Anónimo' : 'Anonymous Advocate')}
                  </h1>
                  {profile.organization_affiliation && (
                    <p className="text-muted-foreground">{profile.organization_affiliation}</p>
                  )}
                  {profile.location_display && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <MapPin className="h-4 w-4" />
                      {profile.location_display}
                    </p>
                  )}
                </div>
                <Badge variant="secondary">
                  {profile.profile_type === 'anonymous' ? (language === 'es' ? 'Anónimo' : 'Anonymous') :
                   profile.profile_type === 'organization' ? (language === 'es' ? 'Organización' : 'Organization') :
                   (language === 'es' ? 'Pseudónimo' : 'Pseudonymous')}
                </Badge>
              </div>

              {profile.bio && (
                <p className="mt-4 text-muted-foreground">{profile.bio}</p>
              )}
            </div>
          </div>

          {/* Issue Areas */}
          {profile.issue_areas && profile.issue_areas.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold mb-2">
                {language === 'es' ? 'Áreas de Interés' : 'Issue Areas'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {profile.issue_areas.map((issue, i) => (
                  <Badge key={i} variant="outline">
                    {getIssueLabel(issue)}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Skills */}
          {profile.skills && profile.skills.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold mb-2">
                {language === 'es' ? 'Habilidades' : 'Skills'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {profile.skills.map((skill, i) => (
                  <Badge key={i} variant="secondary">
                    {skill.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Languages */}
          {profile.languages && profile.languages.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold mb-2">
                {language === 'es' ? 'Idiomas' : 'Languages'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {profile.languages.map(l => l === 'en' ? 'English' : l === 'es' ? 'Español' : l).join(', ')}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 pt-6 border-t flex flex-wrap gap-3">
            {requestSent ? (
              <Button disabled className="flex-1">
                <Check className="mr-2 h-4 w-4" />
                {language === 'es' ? 'Solicitud Enviada' : 'Request Sent'}
              </Button>
            ) : (
              <Button className="flex-1" onClick={() => setConnectDialog(true)} data-testid="connect-btn">
                <UserPlus className="mr-2 h-4 w-4" />
                {language === 'es' ? 'Conectar' : 'Connect'}
              </Button>
            )}
            <Button variant="outline" size="icon" onClick={handleReport} title={language === 'es' ? 'Reportar' : 'Report'}>
              <Flag className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleBlock} title={language === 'es' ? 'Bloquear' : 'Block'}>
              <Ban className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Privacy Notice */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-sm">
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-muted-foreground">
              {language === 'es' 
                ? 'Solo verás más información después de que acepten tu solicitud de conexión'
                : 'You will only see more information after they accept your connection request'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Connect Dialog */}
      <Dialog open={connectDialog} onOpenChange={setConnectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {language === 'es' ? 'Enviar Solicitud de Conexión' : 'Send Connection Request'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Escribe un mensaje explicando tu interés en conectar (mínimo 50 caracteres)'
                : 'Write a message explaining your interest in connecting (minimum 50 characters)'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Tu Mensaje' : 'Your Message'}</Label>
              <Textarea
                placeholder={language === 'es' 
                  ? 'Hola, me interesa conectar porque trabajamos en temas similares de justicia ambiental...'
                  : 'Hi, I\'m interested in connecting because we work on similar environmental justice issues...'}
                value={connectMessage}
                onChange={(e) => setConnectMessage(e.target.value)}
                rows={4}
                data-testid="connect-message"
              />
              <p className={`text-xs ${connectMessage.length < 50 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {connectMessage.length}/50 {language === 'es' ? 'caracteres mínimo' : 'characters minimum'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectDialog(false)}>
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleConnect} 
              disabled={sending || connectMessage.length < 50}
              data-testid="send-request-btn"
            >
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
              {language === 'es' ? 'Enviar Solicitud' : 'Send Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunityProfile;
